import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion, useMotionValue } from 'framer-motion';
import { HeroChatBubble } from './HeroChatBubble';
import { ThinkingDots } from './ThinkingDots';
import { quest } from '../utils/quest';
interface QuestFinaleNotesMobileProps {
  notes: Array<{
    id: number;
    text: string;
  }>;
  thinking: boolean;
  reduce: boolean;
}

/** Same as the first chat: newest + 3 stacked above */
const MAX_STACK = 4;
/** Where the chat anchor sits relative to Pixel — the same offsets as his first chat spot on phones
 *  (Pixel at 30% across, the chat anchor at 40% and 11% of the screen height above his middle) */
const ANCHOR_DX = 0.1;
const ANCHOR_DY = 0.11;

/**
 * Phones, quest finale in the footer: Pixel's notes exactly like his first chat on phones — the big
 * grey bubbles growing upward from his upper right, the newest with its tail pointing down at him,
 * thinking dots tucked under it. Follows him every frame and never runs off the top of the screen.
 */
export function QuestFinaleNotesMobile({
  notes,
  thinking,
  reduce
}: QuestFinaleNotesMobileProps) {
  const left = useMotionValue(-9999);
  const top = useMotionValue(-9999);
  const [room, setRoom] = useState(260);
  const stackRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    let raf = 0;
    let lastRoom = 0;
    const loop = () => {
      const p = quest.pixelPx;
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      if (p.x > -9000) {
        const x = p.x + vw * ANCHOR_DX;
        // Keep the whole stack on screen: push the anchor down if the notes would reach past the top
        const h = stackRef.current?.offsetHeight ?? 0;
        const y = Math.max(p.y - vh * ANCHOR_DY, h + 16);
        left.set(x);
        top.set(y);
        // Room to the right, rounded like the first chat so the notes don't re-wrap while he floats
        const r = Math.max(180, Math.floor((vw - x - 12 - 16) / 20) * 20);
        if (r !== lastRoom) {
          lastRoom = r;
          setRoom(r);
        }
      }
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [left, top]);
  const burst = notes.slice(-MAX_STACK);
  const main = burst[burst.length - 1];
  const older = burst.slice(0, -1);
  return <div className="pointer-events-none fixed inset-0 z-[80]" aria-live="off" data-id="element-218" data-mp="2vnvzj0em24mb">
      <motion.div className="absolute h-0 w-0" style={{
      left,
      top
    }} data-id="element-219" data-mp="3t91tg6xt9chh">
        <div ref={stackRef} role="log" aria-live="polite" aria-label="Pixel says"
      // Bubble sits so its tail's point lands on the anchor, exactly as in the first chat
      className="absolute bottom-[4px] left-[12px] flex w-max flex-col items-start gap-[6px] text-left" style={{
        ['--chat-room' as string]: `${room}px`
      }} data-id="element-220" data-mp="3ob8plkjzcukd">
          <AnimatePresence initial={false} mode="popLayout" data-id="element-221" data-mp="b4qe3gerg3zf">
            {older.map(n => <HeroChatBubble key={n.id} text={n.text} main={false} reduce={reduce} data-id="element-222" data-mp="ifv0oubu54me" />)}
            {main && <HeroChatBubble key={main.id} text={main.text} main reduce={reduce} data-id="element-223" data-mp="ne6aj524rf4m" />}
          </AnimatePresence>
        </div>
        <div className="absolute left-[12px] top-[10px] pl-[15px] pt-[6px]" data-id="element-224" data-mp="scrb55lao0tl">
          <AnimatePresence data-id="element-225" data-mp="xdf2xthcklov">{thinking && <ThinkingDots key="think" reduce={reduce} data-id="element-226" data-mp="xdndhepzzeww" />}</AnimatePresence>
        </div>
      </motion.div>
    </div>;
}